import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Lightbulb, ShieldAlert, FileDown, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { PlanAmelioration } from '../components/qualiopi/amelioration/PlanAmelioration';
import { CartographieRisques } from '../components/qualiopi/risques/CartographieRisques';
import { telecharger } from '../components/qualiopi/shared';

const ONGLETS = {
  plan: { label: "Plan d'amélioration", Icone: Lightbulb, pdf: '/qualiopi/actions/pdf', fichier: 'Plan_amelioration.pdf' },
  risques: { label: 'Analyse des risques', Icone: ShieldAlert, pdf: '/qualiopi/risques/pdf', fichier: 'Cartographie_risques.pdf' },
};

const Chiffre = ({ valeur, label, testId }) => (
  <div className="rounded-xl bg-white/10 px-4 py-3">
    <p className="text-base font-bold sm:text-2xl" data-testid={testId}>{valeur}</p>
    <p className="text-xs text-white/70">{label}</p>
  </div>
);

// Administratif CFA : amélioration continue (indicateur Qualiopi 32) ; Qualiopi n'en garde que les preuves (tableaux PDF)
export default function AmeliorationContinue() {
  const [params, setParams] = useSearchParams();
  const onglet = ONGLETS[params.get('onglet')] ? params.get('onglet') : 'plan';
  const [stats, setStats] = useState(null);
  const charger = () => {
    Promise.all([axios.get('/qualiopi/actions'), axios.get('/qualiopi/risques')])
      .then(([a, r]) => setStats({ ouvertes: a.data.items.filter((x) => ['a_faire', 'en_cours'].includes(x.statut)).length, risques: r.data.items.length,
        eleves: r.data.items.filter((x) => x.niveau === 'eleve').length, revue: r.data.revues[0]?.date }))
      .catch(() => setStats({ erreur: true }));
  };
  useEffect(charger, []);
  if (stats?.erreur) return <div className="p-8 text-red-600" data-testid="amelioration-erreur">Accès non autorisé ou erreur de chargement.</div>;
  const pdf = () => telecharger(ONGLETS[onglet].pdf, ONGLETS[onglet].fichier).catch(() => toast.error('PDF indisponible'));
  return (
    <div className="space-y-6" data-testid="amelioration-continue-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-[#0F172A]" data-testid="amelioration-retour">
        <ArrowLeft className="h-4 w-4" /> Tableau de bord
      </Link>
      <header className="rounded-2xl bg-[#334155] p-6 sm:p-8 text-white shadow-sm">
        <div className="flex items-center gap-4">
          <div className="rounded-xl bg-white/15 p-3"><Lightbulb className="h-7 w-7" /></div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Administratif CFA</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Amélioration continue</h1>
          </div>
        </div>
        <p className="mt-3 text-sm text-white/70">Plan d'amélioration et analyse des risques. Les tableaux PDF sont envoyés comme preuves à l'indicateur 32 de Qualiopi et dans le dossier ZIP.</p>
        <div className="mt-6 grid grid-cols-3 gap-3 sm:max-w-xl">
          <Chiffre valeur={stats ? stats.ouvertes : '…'} label="Actions ouvertes" testId="amelioration-actions-ouvertes" />
          <Chiffre valeur={stats ? `${stats.eleves}/${stats.risques}` : '…'} label="Risques élevés" testId="amelioration-risques-eleves" />
          <Chiffre valeur={stats ? (stats.revue ? new Date(stats.revue).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : 'À faire') : '…'} label="Dernière revue" testId="amelioration-derniere-revue" />
        </div>
      </header>
      <Tabs value={onglet} onValueChange={(v) => setParams(v === 'plan' ? {} : { onglet: v })}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList className="h-auto flex-wrap justify-start gap-1 bg-slate-100 p-1">
            {Object.entries(ONGLETS).map(([k, o]) => <TabsTrigger key={k} value={k} data-testid={`amelioration-onglet-${k}`}><o.Icone className="h-4 w-4 mr-1.5" /> {o.label}</TabsTrigger>)}
          </TabsList>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={pdf} data-testid="amelioration-pdf"><FileDown className="h-4 w-4 mr-1" /> Tableau PDF</Button>
            <Button variant="outline" size="sm" asChild><Link to="/qualiopi" data-testid="amelioration-vers-qualiopi"><ShieldCheck className="h-4 w-4 mr-1" /> Qualiopi</Link></Button>
          </div>
        </div>
        <TabsContent value="plan" className="mt-4"><PlanAmelioration onChanged={charger} /></TabsContent>
        <TabsContent value="risques" className="mt-4"><CartographieRisques onChanged={charger} /></TabsContent>
      </Tabs>
    </div>
  );
}
